/**
 * TITAN Core JavaScript
 * Handles: Quick Add, VAT calculations, Lazy loading, Accordion, Performance
 */

(function() {
  'use strict';

  /* ============================================
     QUICK ADD TO CART
     ============================================ */
  
  class TitanQuickAdd {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-titan-quick-add]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();
          this.addToCart(btn);
        }
      });
    }

    async addToCart(btn) {
      const variantId = btn.dataset.titanQuickAdd;
      if (!variantId) return;

      btn.disabled = true;
      const originalHTML = btn.innerHTML;
      btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="titan-spin"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>`;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId), quantity: 1 }]
          })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>`;
        btn.style.background = 'var(--titan-green)';
        btn.style.borderColor = 'var(--titan-green)';
        btn.style.color = 'white';

        // Update cart count
        this.updateCartCount(1);

        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.style.background = '';
          btn.style.borderColor = '';
          btn.style.color = '';
          btn.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('Quick add error:', error);
        btn.innerHTML = '!';
        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }, 1500);
      }
    }

    updateCartCount(increment) {
      const counters = document.querySelectorAll('.CartCount, [data-cart-count]');
      counters.forEach(counter => {
        const current = parseInt(counter.textContent) || 0;
        counter.textContent = current + increment;
      });

      // Also update the titan header cart badge
      const titanBadge = document.querySelector('[data-titan-cart-icon] span');
      if (titanBadge) {
        const current = parseInt(titanBadge.textContent) || 0;
        titanBadge.textContent = current + increment;
      }
    }
  }

  /* ============================================
     VAT PRICE CALCULATOR
     ============================================ */

  class TitanVATDisplay {
    constructor() {
      this.vatRate = 0.20;
      this.init();
    }

    init() {
      // Listen for variant changes to update prices
      document.addEventListener('change', (e) => {
        if (e.target.matches('.product-variant-id, [name="id"]')) {
          this.onVariantChange(e.target.value);
        }
      });
    }

    formatMoney(cents) {
      return '£' + (cents / 100).toFixed(2);
    }

    calculatePrices(priceInCents, taxIncluded) {
      if (taxIncluded) {
        return {
          incVat: priceInCents,
          exVat: Math.round(priceInCents / 1.2)
        };
      } else {
        return {
          exVat: priceInCents,
          incVat: Math.round(priceInCents * 1.2)
        };
      }
    }

    onVariantChange(variantId) {
      // This would be enhanced with actual variant data
      // For now, it triggers a re-render of price displays
      const event = new CustomEvent('titan:variant-change', {
        detail: { variantId }
      });
      document.dispatchEvent(event);
    }
  }

  /* ============================================
     ACCORDION
     ============================================ */

  class TitanAccordion {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('.titan-accordion-trigger');
        if (trigger) {
          this.toggle(trigger);
        }
      });
    }

    toggle(trigger) {
      const item = trigger.closest('.titan-accordion-item');
      const content = item.querySelector('.titan-accordion-content');
      const isOpen = item.classList.contains('open');

      // Close all siblings (optional: remove for multi-open)
      const accordion = item.closest('.titan-accordion');
      if (accordion) {
        accordion.querySelectorAll('.titan-accordion-item.open').forEach(openItem => {
          if (openItem !== item) {
            openItem.classList.remove('open');
            const openContent = openItem.querySelector('.titan-accordion-content');
            if (openContent) openContent.style.maxHeight = '0';
          }
        });
      }

      if (isOpen) {
        item.classList.remove('open');
        content.style.maxHeight = '0';
      } else {
        item.classList.add('open');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    }
  }

  /* ============================================
     LAZY LOADING IMAGES
     ============================================ */

  class TitanLazyLoad {
    constructor() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => this.onIntersect(entries),
          { rootMargin: '200px 0px' }
        );
        this.observe();
      } else {
        this.loadAll();
      }
    }

    observe() {
      document.querySelectorAll('[data-titan-lazy]').forEach(img => {
        this.observer.observe(img);
      });
    }

    onIntersect(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.load(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }

    load(img) {
      const src = img.dataset.titanLazy;
      if (src) {
        img.src = src;
        img.removeAttribute('data-titan-lazy');
        img.classList.add('titan-loaded');
      }
    }

    loadAll() {
      document.querySelectorAll('[data-titan-lazy]').forEach(img => this.load(img));
    }
  }

  /* ============================================
     SMOOTH SCROLL
     ============================================ */

  class TitanSmoothScroll {
    constructor() {
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="#"]');
        if (link) {
          const target = document.querySelector(link.getAttribute('href'));
          if (target) {
            e.preventDefault();
            const headerHeight = document.querySelector('.titan-header')?.offsetHeight || 64;
            const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }
      });
    }
  }

  /* ============================================
     HEADER SCROLL BEHAVIOR
     ============================================ */

  class TitanHeader {
    constructor() {
      this.header = document.querySelector('[data-titan-header]');
      if (!this.header) return;

      this.lastScroll = 0;
      this.init();
    }

    init() {
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    }

    onScroll() {
      const currentScroll = window.scrollY;

      if (currentScroll > 100) {
        this.header.style.boxShadow = 'var(--titan-shadow-md)';
      } else {
        this.header.style.boxShadow = '';
      }

      this.lastScroll = currentScroll;
    }
  }

  /* ============================================
     INITIALIZE
     ============================================ */

  document.addEventListener('DOMContentLoaded', () => {
    new TitanQuickAdd();
    new TitanVATDisplay();
    new TitanAccordion();
    new TitanLazyLoad();
    new TitanSmoothScroll();
    new TitanHeader();
  });

})();

/* Spin animation for loading states */
const style = document.createElement('style');
style.textContent = `
  @keyframes titan-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .titan-spin {
    animation: titan-spin 0.8s linear infinite;
  }
`;
document.head.appendChild(style);
